/**
 * Swipe Navigation System for ActivityLink Mobile App
 *
 * This module provides intuitive gesture-based navigation for Android devices:
 *
 * SWIPE GESTURES:
 * - Left-to-right swipe from screen edge = Go back (like iOS)
 * - Gesture must start within 50px of left edge to avoid conflicts
 * - Includes visual feedback and haptic vibration
 * - Smart detection prevents conflicts with scrolling and input fields
 *
 * ANDROID BACK BUTTON:
 * - Hardware back button now navigates within app instead of closing it
 * - Follows same exclusion rules as swipe gestures
 * - Provides consistent navigation experience
 *
 * CHAT SCENARIO EXAMPLE:
 * - User on chat list → opens specific chat → swipes left → returns to chat list
 * - User on chat list → opens specific chat → presses hardware back → returns to chat list
 * - No more accidental app exits when trying to navigate back
 *
 * EXCLUDED ROUTES:
 * - Auth pages (login, signup) - can exit app naturally
 * - Create pages - prevent accidental data loss
 * - Search page - avoid conflicts with search gestures
 */

import { useNavigate, useLocation } from "react-router-dom";
import { useCallback, useRef, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { crashlytics } from "../services/firebase";

interface SwipeNavigationOptions {
  enableSwipeBack?: boolean;
  enableSwipeForward?: boolean;
  swipeThreshold?: number;
  velocityThreshold?: number;
  excludeRoutes?: string[];
  onSwipeProgress?: (progress: number, direction: "left" | "right") => void;
  onSwipeStart?: () => void;
  onSwipeEnd?: () => void;
}

interface TouchData {
  startX: number;
  startY: number;
  startTime: number;
  isScrolling: boolean;
  hasStarted: boolean;
}

/**
 * Hook for handling swipe navigation on mobile devices
 * Implements intuitive left/right swipe gestures for navigation
 * Also handles Android hardware back button
 */
export const useSwipeNavigation = (options: SwipeNavigationOptions = {}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation(); // Get the current pathname
  const touchDataRef = useRef<TouchData | null>(null);

  const {
    enableSwipeBack = true,
    enableSwipeForward = false, // Disabled by default as forward navigation is less common
    swipeThreshold = 80, // Minimum distance for swipe
    velocityThreshold = 0.5, // Minimum velocity for swipe
    excludeRoutes = [],
    onSwipeProgress,
    onSwipeStart,
    onSwipeEnd,
  } = options;

  // Check if we're on a mobile platform
  const isMobile = Capacitor.isNativePlatform();
  const lastBackPressRef = useRef<number>(0);

  // Android hardware back button handling
  useEffect(() => {
    if (!isMobile || !Capacitor.isPluginAvailable("App")) return;

    const handleBackButton = async () => {
      if (SwipeNavigationManager.isGestureSuppressed()) {
        return true;
      }

      // Check if current route is excluded
      const isExcluded = excludeRoutes.some((route) =>
        route.endsWith("*")
          ? pathname.startsWith(route.slice(0, -1))
          : pathname === route,
      );

      if (isExcluded) {
        // Let the system handle it (might exit app on certain pages)
        return false;
      }

      // On home page (/), require double-tap to exit instead of going back
      if (pathname === "/" || pathname === "/?") {
        const currentTime = Date.now();
        const timeSinceLastPress = currentTime - lastBackPressRef.current;

        if (timeSinceLastPress < 2000) {
          // Double tap detected - exit app
          lastBackPressRef.current = 0;
          await CapacitorApp.exitApp();
        } else {
          // First tap - wait for second tap
          lastBackPressRef.current = currentTime;
        }
        return true; // Prevent default behavior
      }

      // For non-home pages, handle normal back navigation
      navigate(-1);
      return true; // Prevent default behavior
    };

    // Register the back button handler
    const backButtonPromise = CapacitorApp.addListener(
      "backButton",
      handleBackButton,
    );

    return () => {
      backButtonPromise
        .then((h) => h.remove())
        .catch((e) =>
          crashlytics.logError(
            `Cleanup error in swipe navigation backButton listener: ${e?.message || e}`,
            "swipeNavigation.ts",
            e as any,
          ),
        );
    };
  }, [isMobile, pathname, excludeRoutes, navigate]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!isMobile || !enableSwipeBack) return;
      if (SwipeNavigationManager.isGestureSuppressed()) return;

      const touch = e.touches[0];
      const target = e.target as HTMLElement;

      // Prevent swipe on input elements, text areas, and other interactive elements
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.contentEditable === "true" ||
          target.closest('input, textarea, select, [contenteditable="true"]'))
      ) {
        return;
      }

      // Only allow swipe from the left edge (first 50px) for better UX
      // This prevents accidental swipes when interacting with content
      if (touch.clientX > 50) return;

      touchDataRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isScrolling: false,
        hasStarted: false,
      };
    },
    [isMobile, enableSwipeBack, onSwipeStart],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!touchDataRef.current || !isMobile) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchDataRef.current.startX;
      const deltaY = Math.abs(touch.clientY - touchDataRef.current.startY);

      // Determine if this is a vertical scroll
      if (deltaY > Math.abs(deltaX) && deltaY > 10) {
        touchDataRef.current.isScrolling = true;
        onSwipeEnd?.();
        return;
      }

      // Calculate swipe progress for visual feedback
      if (Math.abs(deltaX) > 5 && !touchDataRef.current.isScrolling) {
        // Call onSwipeStart here instead of in handleTouchStart
        // Use a ref or flag to ensure it only fires once per gesture
        if (!touchDataRef.current.hasStarted) {
          onSwipeStart?.();
          touchDataRef.current.hasStarted = true;
        }

        const progress = Math.min(Math.abs(deltaX) / swipeThreshold, 1);
        const direction = deltaX > 0 ? "left" : "right";
        onSwipeProgress?.(progress, direction);
      }
    },
    [isMobile, swipeThreshold, onSwipeProgress, onSwipeEnd, onSwipeStart],
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchDataRef.current || !isMobile) {
        // Only fire if the UI actually showed a swipe indicator
        if (touchDataRef.current?.hasStarted) {
          onSwipeEnd?.();
        }
        touchDataRef.current = null;
        return;
      }

      if (touchDataRef.current.isScrolling) {
        if (touchDataRef.current?.hasStarted) {
          onSwipeEnd?.();
        }
        touchDataRef.current = null;
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchDataRef.current.startX;
      const deltaY = Math.abs(touch.clientY - touchDataRef.current.startY);
      const deltaTime = Date.now() - touchDataRef.current.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;

      // Always call onSwipeEnd to hide indicators
      if (touchDataRef.current.hasStarted) {
        onSwipeEnd?.();
      }

      // Check if this is a horizontal swipe (not vertical scroll)
      // More strict thresholds for better accuracy
      if (
        deltaY < 30 &&
        Math.abs(deltaX) > swipeThreshold &&
        velocity > velocityThreshold
      ) {
        // Check if current route is excluded
        const isExcluded = excludeRoutes.some((route) =>
          route.endsWith("*")
            ? pathname.startsWith(route.slice(0, -1))
            : pathname === route,
        );

        if (isExcluded) {
          touchDataRef.current = null;
          return;
        }

        // Left swipe (positive deltaX) = go back
        if (deltaX > 0 && enableSwipeBack) {
          // Add haptic feedback if available
          if ("vibrate" in navigator) {
            navigator.vibrate(50);
          }

          // Small delay to provide visual feedback
          setTimeout(() => {
            navigate(-1);
          }, 50);
        }
        // Right swipe (negative deltaX) = go forward
        else if (deltaX < 0 && enableSwipeForward) {
          if ("vibrate" in navigator) {
            navigator.vibrate(50);
          }

          setTimeout(() => {
            navigate(1);
          }, 50);
        }
      }

      touchDataRef.current = null;
    },
    [
      navigate,
      isMobile,
      enableSwipeBack,
      enableSwipeForward,
      swipeThreshold,
      velocityThreshold,
      excludeRoutes,
      onSwipeEnd,
    ],
  );

  const handleTouchCancel = useCallback(() => {
    if (touchDataRef.current?.hasStarted) {
      onSwipeEnd?.();
    }
    touchDataRef.current = null;
  }, [onSwipeEnd]);

  const addSwipeListeners = useCallback(() => {
    if (!isMobile) return;

    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
    });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, isMobile]);

  return {
    addSwipeListeners,
    isMobile,
  };
};

/**
 * Navigation history manager for better swipe navigation experience
 */
export class SwipeNavigationManager {
  private static navigationStack: string[] = [];
  private static currentIndex: number = -1;
  private static gestureSuppressionTokens: Set<string> = new Set();

  static pushRoute(route: string) {
    // Don't add duplicate consecutive routes
    if (this.navigationStack[this.currentIndex] === route) return;

    // If we're not at the end of the stack, clear everything after current position
    if (this.currentIndex < this.navigationStack.length - 1) {
      this.navigationStack = this.navigationStack.slice(
        0,
        this.currentIndex + 1,
      );
    }

    this.navigationStack.push(route);
    this.currentIndex = this.navigationStack.length - 1;

    // Keep stack manageable
    if (this.navigationStack.length > 20) {
      this.navigationStack = this.navigationStack.slice(-15);
      this.currentIndex = this.navigationStack.length - 1;
    }
  }

  static canGoBack(): boolean {
    return this.currentIndex > 0;
  }

  static canGoForward(): boolean {
    return this.currentIndex < this.navigationStack.length - 1;
  }

  static getPreviousRoute(): string | null {
    if (!this.canGoBack()) return null;
    return this.navigationStack[this.currentIndex - 1];
  }

  static getNextRoute(): string | null {
    if (!this.canGoForward()) return null;
    return this.navigationStack[this.currentIndex + 1];
  }

  static goBack(): string | null {
    if (!this.canGoBack()) return null;
    this.currentIndex--;
    return this.navigationStack[this.currentIndex];
  }

  static goForward(): string | null {
    if (!this.canGoForward()) return null;
    this.currentIndex++;
    return this.navigationStack[this.currentIndex];
  }

  static clear() {
    this.navigationStack = [];
    this.currentIndex = -1;
    this.gestureSuppressionTokens.clear();
  }

  static setGestureSuppressed(token: string, suppressed: boolean) {
    if (!token) return;
    if (suppressed) {
      this.gestureSuppressionTokens.add(token);
      return;
    }
    this.gestureSuppressionTokens.delete(token);
  }

  static isGestureSuppressed(): boolean {
    return this.gestureSuppressionTokens.size > 0;
  }

  static getStack() {
    return {
      stack: [...this.navigationStack],
      currentIndex: this.currentIndex,
    };
  }
}
